import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MilaLogo } from "@/components/MilaLogo";
import { api, resolveImageUrl } from "@/lib/axios";
import { Product } from "@/types";
import { Image as ImageIcon, ArrowRight, Star } from "lucide-react";
import { STORE_WHATSAPP_NUMBER } from "@/lib/constants";

export default function LandingPage() {
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadFeatured();
  }, []);

  const loadFeatured = async () => {
    try {
      setIsLoading(true);
      // Traer productos destacados activos
      const res = await api.get<Product[]>('/products?featured=true');
      let prods = Array.isArray(res.data) ? res.data : [];
      
      // Si no hay ninguno marcado explícitamente como destacado, traer los primeros públicos del catálogo
      if (prods.length === 0) {
        const fallbackRes = await api.get<Product[]>('/products');
        prods = Array.isArray(fallbackRes.data) ? fallbackRes.data.slice(0, 4) : [];
      }
      setFeaturedProducts(prods);
    } catch {
      setFeaturedProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen font-sans">
      {/* Hero Section */}
      <section className="px-4 py-20 md:py-28 bg-card text-center flex flex-col items-center justify-center border-b border-border/50">
        <div className="mb-6 flex justify-center animate-in fade-in zoom-in-95 duration-500">
          <MilaLogo tight className="h-16 md:h-20 w-auto text-primary" />
        </div>
        <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 max-w-3xl text-foreground">
          Diseño 3D con propósito
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl leading-relaxed">
          Creamos piezas únicas mediante impresión 3D, combinando estética Japandi, texturas minimalistas y funcionalidad.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <Button asChild size="lg" className="text-base px-8 gap-2">
            <Link to="/tienda">
              Ver Tienda <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="text-base px-8">
            <a
              href={`https://wa.me/${STORE_WHATSAPP_NUMBER}?text=${encodeURIComponent('Hola Mila! Quería consultar por sus piezas de diseño.')}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Contactar por WhatsApp
            </a>
          </Button>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-background px-4">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Diseño propio",
                desc: "Cada pieza es diseñada desde cero pensando en la estética Japandi y la utilidad cotidiana.",
              },
              {
                title: "Fabricación directa",
                desc: "Impreso en nuestro taller con filamentos biodegradables de primera calidad y acabado artesanal.",
              },
              {
                title: "Personalización",
                desc: "Elegí entre diversas bases, tonos cálidos y texturas orgánicas a tu medida.",
              },
            ].map((feature, i) => (
              <Card key={i} className="border-border/60 shadow-xs bg-muted/20">
                <CardContent className="p-8 text-center space-y-2">
                  <h3 className="text-xl font-semibold">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Productos Destacados en Vivo */}
      <section className="py-20 bg-card px-4 border-t border-border/50">
        <div className="container mx-auto text-center max-w-6xl">
          <div className="mb-12">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-2">
              <Star className="h-3.5 w-3.5 fill-primary" /> Selección de Taller
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-foreground">
              Productos Destacados
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Las piezas más elegidas para transformar tus espacios
            </p>
          </div>

          {isLoading ? (
            <div className="py-16 text-muted-foreground text-sm">Cargando creaciones destacadas...</div>
          ) : featuredProducts.length === 0 ? (
            <div className="py-16 border border-dashed rounded-2xl text-muted-foreground text-sm">
              Pronto podrás ver aquí nuestras creaciones destacadas.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.map((p) => {
                const price = p.isOnSale && p.promotionalPrice ? p.promotionalPrice : p.salePrice;
                return (
                  <Link key={p.id} to={`/tienda/${p.slug || p.id}`}>
                    <Card className="h-full overflow-hidden border border-border/70 hover:shadow-md hover:border-primary/50 transition-all flex flex-col justify-between group cursor-pointer text-left bg-background">
                      <div>
                        <div className="aspect-square bg-muted/30 relative overflow-hidden flex items-center justify-center">
                          {p.mainImageUrl ? (
                            <img
                              src={resolveImageUrl(p.mainImageUrl)}
                              alt={p.name}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          ) : (
                            <ImageIcon className="h-12 w-12 text-muted-foreground/30" />
                          )}
                          {p.isFeatured && (
                            <Badge className="absolute top-2 left-2 bg-amber-500 text-white text-[10px] shadow-sm gap-1">
                              <Star className="h-3 w-3 fill-white" /> Destacado
                            </Badge>
                          )}
                          {p.isOnSale && (
                            <Badge className="absolute top-2 right-2 bg-destructive text-destructive-foreground text-[10px] shadow-sm">
                              OFERTA
                            </Badge>
                          )}
                        </div>

                        <CardContent className="p-4 space-y-1">
                          <h4 className="font-semibold text-base line-clamp-1 group-hover:text-primary transition-colors">
                            {p.name}
                          </h4>
                          <p className="text-xs text-muted-foreground line-clamp-2 min-h-[32px]">
                            {p.shortDescription || 'Diseño sustentable para tus espacios.'}
                          </p>
                        </CardContent>
                      </div>

                      <div className="px-4 pb-4 pt-2 border-t border-border/40 flex justify-between items-baseline">
                        <div>
                          <span className="font-bold text-lg text-primary">
                            ${price.toLocaleString('es-AR')}
                          </span>
                          {p.isOnSale && p.promotionalPrice && (
                            <span className="text-xs line-through text-muted-foreground ml-1.5">
                              ${p.salePrice.toLocaleString('es-AR')}
                            </span>
                          )}
                        </div>
                        <span className="text-[11px] text-muted-foreground font-medium">
                          Ver detalles →
                        </span>
                      </div>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}

          <div className="mt-12">
            <Button asChild variant="outline" size="lg">
              <Link to="/tienda">Explorar Todo el Catálogo</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
